import React, { useState, useEffect } from "react";
import { displayToast } from "../utils/displayToast";
import { useErrorHandler } from "../utils/ErrorHandler";
import { createRole, updateRole } from "../apis/role";
import { fetchDepartments } from "../apis/department";

const RoleFormPopup = ({
  isEditing,
  currentRole,
  permissionStructure,
  onClose,
  onSave,
}) => {
  const { handleErrorResponse } = useErrorHandler();
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [expandedTabs, setExpandedTabs] = useState([]);
  const [expandedChildren, setExpandedChildren] = useState([]);
  const [roleForm, setRoleForm] = useState({
    name: "",
    department_id: "",
    level: "",
    can_access_admin_dashboard: false,
    tabs: [],
  });

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const response = await fetchDepartments();
        if (response?.description) {
          handleErrorResponse(response.description);
        } else {
          setDepartments(Array.isArray(response) ? response : []);
        }
      } catch (error) {
        displayToast("Failed to load departments: " + error.message, "error");
      }
    };

    loadDepartments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (permissionStructure?.items?.length) {
      setRoleForm((prev) => {
        const newTabs = permissionStructure.items.map((tab) => ({
          name: tab.display_title,
          permissions: [],
          parent_menu_id: tab.parent_menu_id,
          icon_url: tab.icon_url,
          order_no: tab.order_no || 1,
          is_admin: tab.dashboard_type === "admin",
          children: (tab.child_menu || []).map((child) => ({
            name: child.display_title,
            permissions: [],
            availablePermissions: child.permissions || [],
            menu_item_id: child.menu_item_id,
            menu_code: child.menu_code,
            parent_menu_id: child.parent_menu_id,
            order_no: child.order_no || 1,
          })),
        }));

        return {
          ...prev,
          name: isEditing && currentRole ? currentRole.name : "",
          department_id:
            isEditing && currentRole ? currentRole.department_id || "" : "",
          level:
            isEditing && currentRole ? currentRole.level || "" : "",
          can_access_admin_dashboard:
            isEditing && currentRole
              ? currentRole.can_access_admin_dashboard
              : false,
          tabs: newTabs,
        };
      });
    }
  }, [permissionStructure, isEditing, currentRole]);

  useEffect(() => {
    if (isEditing && currentRole && permissionStructure?.items?.length) {
      const formattedRole = {
        name: currentRole.name,
        department_id: currentRole.department_id || "",
        level: currentRole.level || "",
        can_access_admin_dashboard:
          currentRole.can_access_admin_dashboard || false,
        tabs: permissionStructure.items.map((tab) => {
          const matchingParentMenu = currentRole.menu_controls?.find(
            (menuControl) => menuControl.parent_menu_id === tab.parent_menu_id
          );

          return {
            name: tab.display_title,
            permissions: matchingParentMenu
              ? ["read", "edit", "insert", "delete", "print", "approve"]
              : [],
            parent_menu_id: tab.parent_menu_id,
            icon_url: tab.icon_url,
            order_no: tab.order_no || 1,
            is_admin: tab.dashboard_type === "admin",
            children: tab.child_menu.map((child) => {
              const matchingChildMenu = matchingParentMenu?.child_menu?.find(
                (childMenu) => childMenu.menu_item_id === child.menu_item_id
              );

              const permissions = [];
              if (matchingChildMenu?.rights) {
                if (matchingChildMenu.rights.can_view) permissions.push("read");
                if (matchingChildMenu.rights.can_edit) permissions.push("edit");
                if (matchingChildMenu.rights.can_add)
                  permissions.push("insert");
                if (matchingChildMenu.rights.can_delete)
                  permissions.push("delete");
                if (matchingChildMenu.rights.can_print)
                  permissions.push("print");
                if (matchingChildMenu.rights.can_approve)
                  permissions.push("approve");
              }

              return {
                name: child.display_title,
                permissions,
                availablePermissions: child.permissions || [],
                menu_item_id: child.menu_item_id,
                menu_code: child.menu_code,
                parent_menu_id: child.parent_menu_id,
                order_no: child.order_no || 1,
              };
            }),
          };
        }),
      };

      setRoleForm(formattedRole);
    }
  }, [isEditing, currentRole, permissionStructure]);

  const toggleParentTab = (parentTab, isChecked) => {
    setRoleForm((prevForm) => {
      const updatedTabs = [...prevForm.tabs];
      const parentIndex = updatedTabs.findIndex(
        (tab) => tab.name === parentTab.display_title
      );

      if (parentIndex !== -1) {
        updatedTabs[parentIndex].permissions = isChecked
          ? ["read", "edit", "insert", "delete", "print", "approve"]
          : [];

        updatedTabs[parentIndex].children.forEach((child) => {
          child.permissions = isChecked
            ? ["read", "edit", "insert", "delete", "print", "approve"]
            : [];
        });
      }

      return { ...prevForm, tabs: updatedTabs };
    });
  };

  const toggleChildTab = (parentTab, childTab, checked) => {
    setRoleForm((prevForm) => {
      const updatedTabs = [...prevForm.tabs];
      const parentIndex = updatedTabs.findIndex(
        (tab) => tab.name === parentTab.display_title
      );

      if (parentIndex === -1) return prevForm;

      const childIndex = updatedTabs[parentIndex].children.findIndex(
        (child) => child.name === childTab.display_title
      );

      if (childIndex === -1) return prevForm;

      updatedTabs[parentIndex].children[childIndex].permissions = checked
        ? [
            ...(childTab.availablePermissions || [
              "read",
              "edit",
              "insert",
              "delete",
              "print",
              "approve",
            ]),
          ]
        : [];

      const hasSelectedChildren = updatedTabs[parentIndex].children.some(
        (child) => child.permissions.length > 0
      );

      updatedTabs[parentIndex].permissions = hasSelectedChildren
        ? ["read", "edit", "insert", "delete", "print", "approve"]
        : [];

      return {
        ...prevForm,
        tabs: updatedTabs,
      };
    });
  };

  const togglePermission = (parentTab, childTab, permission, isChecked) => {
    setRoleForm((prevForm) => {
      const updatedTabs = [...prevForm.tabs];
      const parentIndex = updatedTabs.findIndex(
        (tab) => tab.name === parentTab.display_title
      );

      if (parentIndex !== -1) {
        updatedTabs[parentIndex] = { ...updatedTabs[parentIndex] };

        updatedTabs[parentIndex].children = [
          ...updatedTabs[parentIndex].children,
        ];

        const childIndex = updatedTabs[parentIndex].children.findIndex(
          (child) => child.name === childTab.display_title
        );

        if (childIndex !== -1) {
          updatedTabs[parentIndex].children[childIndex] = {
            ...updatedTabs[parentIndex].children[childIndex],
          };

          let permissions = [
            ...updatedTabs[parentIndex].children[childIndex].permissions,
          ];

          if (isChecked) {
            if (!permissions.includes(permission)) {
              permissions.push(permission);
              if (updatedTabs[parentIndex].permissions.length === 0) {
                updatedTabs[parentIndex].permissions = [
                  "read",
                  "edit",
                  "insert",
                  "delete",
                  "print",
                  "approve",
                ];
              }
            }
          } else {
            permissions = permissions.filter((p) => p !== permission);
            if (permissions.length === 0) {
              const hasSelectedChildren = updatedTabs[
                parentIndex
              ].children.some(
                (child) =>
                  child.permissions.length > 0 &&
                  child !== updatedTabs[parentIndex].children[childIndex]
              );
              if (!hasSelectedChildren) {
                updatedTabs[parentIndex].permissions = [];
              }
            }
          }

          updatedTabs[parentIndex].children[childIndex].permissions =
            permissions;
        }
      }

      return {
        ...prevForm,
        tabs: updatedTabs,
      };
    });
  };

  const toggleExpandParent = (parentTabName) => {
    setExpandedTabs((prev) =>
      prev.includes(parentTabName)
        ? prev.filter((tab) => tab !== parentTabName)
        : [...prev, parentTabName]
    );
  };

  const toggleExpandChild = (childTabName) => {
    setExpandedChildren((prev) =>
      prev.includes(childTabName)
        ? prev.filter((tab) => tab !== childTabName)
        : [...prev, childTabName]
    );
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      displayToast("Please enter a role name.");
      return;
    }

    if (!roleForm.department_id) {
      displayToast("Please select a department.");
      return;
    }

    if (!roleForm.level || Number(roleForm.level) < 1) {
      displayToast("Please enter a valid level (minimum 1).");
      return;
    }

    const hasPermissions = roleForm.tabs.some(
      (tab) =>
        tab.permissions.length > 0 ||
        tab.children.some((child) => child.permissions.length > 0)
    );

    if (!hasPermissions) {
      displayToast("Please select at least one permission.");
      return;
    }

    const transformMenus = (tabs) =>
      tabs
        .filter(
          (tab) =>
            tab.permissions.length > 0 ||
            tab.children.some((child) => child.permissions.length > 0)
        )
        .map((tab) => {
          return {
            parent_menu_id: tab.parent_menu_id,
            display_title: tab.name,
            name: tab.name,
            icon_url: tab.icon_url,
            order_no: tab.order_no || 1,
            child_menu: tab.children
              .filter((child) => child.permissions.length > 0)
              .map((child) => ({
                parent_menu_id: child.parent_menu_id,
                menu_item_id: child.menu_item_id,
                name: child.name,
                display_title: child.name,
                menu_code: child.menu_code,
                order_no: child.order_no || 1,
                status: "active",
                type: "transaction",
                rights: {
                  can_edit: child.permissions.includes("edit"),
                  can_add: child.permissions.includes("insert"),
                  can_delete: child.permissions.includes("delete"),
                  can_view: child.permissions.includes("read"),
                  can_print: child.permissions.includes("print"),
                  can_approve: child.permissions.includes("approve"),
                },
              })),
          };
        });

    const transformedRole = {
      name: roleForm.name,
      department_id: Number(roleForm.department_id),
      level: Number(roleForm.level),
      can_access_admin_dashboard: roleForm.can_access_admin_dashboard,
      user_menu_controls: transformMenus(
        roleForm.tabs.filter((tab) => !tab.is_admin)
      ),
      admin_menu_controls: transformMenus(
        roleForm.tabs.filter((tab) => tab.is_admin)
      ),
    };

    try {
      setIsLoading(true);
      let response;

      if (isEditing && currentRole) {
        transformedRole.id = currentRole.id;
        response = await updateRole(transformedRole, currentRole.id);
      } else {
        response = await createRole(transformedRole);
      }

      if (response.name) {
        displayToast(
          isEditing ? "Role updated successfully!" : "Role added successfully!",
          "success"
        );
        onSave && onSave();
        onClose();
      } else if (response.description) {
        handleErrorResponse(response.description);
      } else {
        displayToast(
          isEditing ? "Failed to update role" : "Failed to add role"
        );
      }
    } catch (error) {
      displayToast("Error: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="popup-overlay">
      <div className="popup-container">
        <div className="popup-header">
          <h3>{isEditing ? "Edit Role" : "Add New Role"}</h3>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="popup-content">
          <input
            type="text"
            placeholder="Role Name"
            className="role-input"
            value={roleForm.name}
            onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
          />

          <select
            className="role-input department-select"
            value={roleForm.department_id}
            onChange={(e) =>
              setRoleForm({ ...roleForm, department_id: e.target.value })
            }
            required
          >
            <option value="">Select Department</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name} ({department.code})
              </option>
            ))}
          </select>

          <label className="priority-level-label" htmlFor="role-priority-level">
            Priority Level <span>(1 = highest priority)</span>
          </label>
          <input
            type="number"
            id="role-priority-level"
            placeholder="Enter priority level"
            className="role-input"
            min="1"
            value={roleForm.level}
            onChange={(e) => setRoleForm({ ...roleForm, level: e.target.value })}
          />

          <div className="admin-access-checkbox">
            <label>
              <input
                type="checkbox"
                checked={roleForm.can_access_admin_dashboard}
                onChange={(e) =>
                  setRoleForm({
                    ...roleForm,
                    can_access_admin_dashboard: e.target.checked,
                  })
                }
              />
              <span>Can Access Admin Dashboard</span>
            </label>
          </div>

          <h4 className="permissions-title">Permissions</h4>
          {permissionStructure?.items?.length > 0 ? (
            <div>
              {permissionStructure.items.map((parentTab) => (
                <div
                  key={parentTab.display_title}
                  className="parent-tab-section"
                >
                  <div className="flex">
                    <label
                      className="parent-tab-checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={
                          roleForm.tabs.find(
                            (tab) => tab.name === parentTab.display_title
                          )?.permissions.length > 0
                        }
                        onChange={(e) =>
                          toggleParentTab(parentTab, e.target.checked)
                        }
                      />
                    </label>
                    <span
                      className="parent-tab-title"
                      onClick={() =>
                        toggleExpandParent(parentTab.display_title)
                      }
                    >
                      {parentTab.display_title}
                      <span className="expand-indicator">
                        {expandedTabs.includes(parentTab.display_title)
                          ? "▼"
                          : "▶"}
                      </span>
                    </span>
                  </div>

                  {expandedTabs.includes(parentTab.display_title) && (
                    <div className="child-tab-section">
                      {parentTab.child_menu.map((childTab) => (
                        <div
                          key={childTab.display_title}
                          className="child-tab-item"
                        >
                          <div className="flex">
                            <label
                              className="child-tab-checkbox"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  roleForm.tabs
                                    .find(
                                      (tab) =>
                                        tab.name === parentTab.display_title
                                    )
                                    ?.children.find(
                                      (child) =>
                                        child.name === childTab.display_title
                                    )?.permissions.length > 0
                                }
                                onChange={(e) =>
                                  toggleChildTab(
                                    parentTab,
                                    childTab,
                                    e.target.checked
                                  )
                                }
                              />
                            </label>
                            <span
                              className="child-tab-title"
                              onClick={() =>
                                toggleExpandChild(childTab.display_title)
                              }
                            >
                              {childTab.display_title}
                              <span className="expand-indicator">
                                {expandedChildren.includes(
                                  childTab.display_title
                                )
                                  ? "▼"
                                  : "▶"}
                              </span>
                            </span>
                          </div>

                          {expandedChildren.includes(
                            childTab.display_title
                          ) && (
                            <div className="permissions-section">
                              {(childTab.permissions?.length > 0
                                ? childTab.permissions
                                : [
                                    "read",
                                    "edit",
                                    "insert",
                                    "delete",
                                    "print",
                                    "approve",
                                  ]
                              ).map((permission) => (
                                <label
                                  key={permission}
                                  className="permission-checkbox"
                                >
                                  <input
                                    type="checkbox"
                                    checked={roleForm.tabs
                                      .find(
                                        (tab) =>
                                          tab.name === parentTab.display_title
                                      )
                                      ?.children.find(
                                        (child) =>
                                          child.name === childTab.display_title
                                      )
                                      ?.permissions.includes(permission)}
                                    onChange={(e) =>
                                      togglePermission(
                                        parentTab,
                                        childTab,
                                        permission,
                                        e.target.checked
                                      )
                                    }
                                  />
                                  <span>
                                    {permission.charAt(0).toUpperCase() +
                                      permission.slice(1)}
                                  </span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p>No permissions available.</p>
          )}
        </div>
        <div className="popup-footer">
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
          <button onClick={handleSaveRole} className="save-button">
            {isLoading ? (
              <span className="loader"></span>
            ) : isEditing ? (
              "Update Role"
            ) : (
              "Add Role"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleFormPopup;
